/**
 * Remembers which Optro instance the browser arrived from, so `/signin` can go
 * straight into "Sign in with Optro" for it.
 *
 * Captured once at boot (`main.tsx`) rather than read on the sign-in page:
 * `document.referrer` belongs to the last full page load, and the most common
 * way a returning Optro user reaches `/signin` is `clearAuthAndRedirect`'s
 * `window.location.href = "/signin"` after their token lapsed. That reload
 * replaces the referrer with the Midship page they were on, so reading it on
 * `/signin` would miss exactly the user this exists for.
 *
 * sessionStorage, not localStorage: `SignIn` wipes localStorage on mount, and
 * the value should not outlive the tab that came from Optro.
 */
import { optroBaseUrlFromReferrer } from "./optroDomain";

const REFERRER_KEY = "midship:optroReferrerBaseUrl";
const ATTEMPTED_KEY = "optro_referrer_auto_signin_attempted";

/**
 * The OAuth callback page. Optro redirects the browser here, so its load
 * reports Optro as the referrer even though it is the end of a sign-in, not a
 * new arrival.
 */
const OPTRO_CALLBACK_PATH = "/auth/optrooauth/callback";

/**
 * Whether this page load is a fresh arrival: a followed link or typed URL,
 * not a reload or back/forward, which replay the original entry's referrer.
 */
function isFreshNavigation(): boolean {
  const [entry] = performance.getEntriesByType("navigation");
  return (
    entry instanceof PerformanceNavigationTiming && entry.type === "navigate"
  );
}

/**
 * Stash the Optro instance behind `document.referrer`, if it is one. A
 * non-Optro referrer leaves an earlier capture in place: a reload inside
 * Midship reports Midship as the referrer and must not erase where the tab
 * originally came from.
 *
 * A fresh arrival from Optro also gives the tab its auto sign-in attempt back.
 * Otherwise an attempt the user abandoned (say, by leaving the Optro login
 * page) would leave every later arrival in the same tab on the manual form.
 * The callback page and reload/back-forward loads are excluded: they replay
 * an Optro referrer after an attempt, and re-arming there would bounce a
 * failed sign-in or a Back press straight back into Optro.
 */
export function captureOptroReferrer(): void {
  const baseUrl = optroBaseUrlFromReferrer(document.referrer);
  if (!baseUrl) return;
  try {
    sessionStorage.setItem(REFERRER_KEY, baseUrl);
    if (
      window.location.pathname !== OPTRO_CALLBACK_PATH &&
      isFreshNavigation()
    ) {
      sessionStorage.removeItem(ATTEMPTED_KEY);
    }
  } catch {
    // Private mode / full storage: the user just gets the manual dialog.
  }
}

/** The captured Optro instance base URL, or `null` when none was captured. */
export function capturedOptroReferrer(): string | null {
  try {
    return sessionStorage.getItem(REFERRER_KEY);
  } catch {
    return null;
  }
}

/**
 * Use up the tab's auto sign-in attempt without making one. Called on logout:
 * otherwise `/signin` right after a logout would start Optro sign-in again and,
 * with the Optro session still alive, sign the user straight back in. The
 * captured instance stays, so the page still offers "Sign in with Optro" for
 * it; a later fresh arrival from Optro re-arms the attempt. A token expiry does
 * not come through here, so it still re-enters Optro sign-in.
 */
export function suppressOptroAutoSignIn(): void {
  try {
    sessionStorage.setItem(ATTEMPTED_KEY, "1");
  } catch {
    // Without storage there is no auto sign-in to suppress.
  }
}

/**
 * Claim the tab's single auto sign-in attempt. Returns `false` when it was
 * already used (or storage is unavailable, which leaves no way to stop a
 * failed attempt from looping back through `/signin`).
 */
export function claimOptroAutoSignInAttempt(): boolean {
  try {
    if (sessionStorage.getItem(ATTEMPTED_KEY)) return false;
    sessionStorage.setItem(ATTEMPTED_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

/**
 * Give the tab its attempt back after a successful Optro sign-in, so a later
 * token expiry in the same tab re-enters Optro sign-in instead of stranding
 * the user on the manual form.
 */
export function resetOptroAutoSignInAttempt(): void {
  try {
    sessionStorage.removeItem(ATTEMPTED_KEY);
  } catch {
    // Nothing to reset if storage is unavailable.
  }
}
